// Блог: статьи лежат в src/content/blog/<slug>.<lang>.md и встраиваются в сборку.
// Формат файла: шапка между строками «---» (title, description, h1, date, hero, heroAlt;
// для чужого фото обложки — heroCredit и heroCreditUrl), дальше упрощённый markdown:
// «## » и «### » — подзаголовки, «- » — пункт списка, «![alt](file.jpg)» — фото из public/blog/
// (чужое фото: «![alt](file.jpg "Автор, лицензия|https://страница-файла")»),
// «[текст](/путь/){cta}» — кнопка, **жирный** и [ссылка](/путь/ или https://…) внутри текста.
// Внешние ссылки открываются в новой вкладке. Перенос строки внутри абзаца сохраняется.

import { LANGS, SITE_URL, type Lang } from './site';

export type Inline = { t: 'text'; v: string } | { t: 'b'; v: string } | { t: 'a'; v: string; href: string } | { t: 'br' };

export type Block =
  | { t: 'h2'; text: string }
  | { t: 'h3'; text: string }
  | { t: 'p'; inl: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'img'; src: string; alt: string; credit?: string; creditUrl?: string }
  | { t: 'cta'; text: string; href: string };

export interface Post {
  slug: string;
  lang: Lang;
  title: string;
  description: string;
  h1: string;
  date: string;
  hero: string;
  heroAlt: string;
  heroCredit?: string;
  heroCreditUrl?: string;
  blocks: Block[];
}

/** Путь к фото статьи: файлы лежат в public/blog/. */
export const blogImage = (file: string): string => `/blog/${file}`;

function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push({ t: 'text', v: s.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ t: 'b', v: m[1] });
    else out.push({ t: 'a', v: m[2], href: m[3] });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ t: 'text', v: s.slice(last) });
  return out;
}

function parseBody(body: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: Inline[][] | null = null;

  const flush = () => {
    if (para.length) {
      const inl: Inline[] = [];
      para.forEach((line, i) => {
        if (i) inl.push({ t: 'br' });
        inl.push(...parseInline(line));
      });
      blocks.push({ t: 'p', inl });
      para = [];
    }
    if (list) {
      blocks.push({ t: 'ul', items: list });
      list = null;
    }
  };

  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim();
    const img = line.match(/^!\[(.*?)\]\((\S+?)(?:\s+"([^"|]*)(?:\|([^"]*))?")?\)$/);
    const cta = line.match(/^\[(.+?)\]\((.+?)\)\{cta\}$/);
    if (!line) flush();
    else if (img) {
      flush();
      const block: Block = { t: 'img', alt: img[1], src: blogImage(img[2]) };
      if (img[3]) block.credit = img[3];
      if (img[4]) block.creditUrl = img[4];
      blocks.push(block);
    } else if (cta) {
      flush();
      blocks.push({ t: 'cta', text: cta[1], href: cta[2] });
    } else if (line.startsWith('### ')) {
      flush();
      blocks.push({ t: 'h3', text: line.slice(4) });
    } else if (line.startsWith('## ')) {
      flush();
      blocks.push({ t: 'h2', text: line.slice(3) });
    } else if (line.startsWith('- ')) {
      if (para.length) flush();
      (list ??= []).push(parseInline(line.slice(2)));
    } else {
      if (list) flush();
      para.push(line);
    }
  }
  flush();
  return blocks;
}

function parseFile(path: string, raw: string): Post {
  const name = path.split('/').pop()!.replace(/\.md$/, '');
  const dot = name.lastIndexOf('.');
  const slug = name.slice(0, dot);
  const lang = name.slice(dot + 1) as Lang;
  const m = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!m || !(LANGS as readonly string[]).includes(lang)) throw new Error(`blog: неверный файл ${path}`);
  const fm: Record<string, string> = {};
  for (const line of m[1].split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) fm[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  for (const key of ['title', 'description', 'h1', 'date', 'hero', 'heroAlt']) {
    if (!fm[key]) throw new Error(`blog: в ${path} нет поля ${key}`);
  }
  return {
    slug,
    lang,
    title: fm.title,
    description: fm.description,
    h1: fm.h1,
    date: fm.date,
    hero: blogImage(fm.hero),
    heroAlt: fm.heroAlt,
    blocks: parseBody(m[2]),
    ...(fm.heroCredit ? { heroCredit: fm.heroCredit } : {}),
    ...(fm.heroCreditUrl ? { heroCreditUrl: fm.heroCreditUrl } : {}),
  };
}

const files = import.meta.glob('../../content/blog/*.md', { query: '?raw', import: 'default', eager: true }) as Record<
  string,
  string
>;

const POSTS: Post[] = Object.entries(files)
  .map(([path, raw]) => parseFile(path, raw))
  .sort((a, b) => b.date.localeCompare(a.date) || a.slug.localeCompare(b.slug));

/** Статьи на языке, новые сверху. */
export function listPosts(lang: Lang): Post[] {
  return POSTS.filter((p) => p.lang === lang);
}

export function getPost(slug: string | undefined, lang: Lang): Post | undefined {
  return POSTS.find((p) => p.slug === slug && p.lang === lang);
}

/** Языки, на которых есть статья с этим слагом (для hreflang и sitemap). */
export function postLangs(slug: string): Lang[] {
  return LANGS.filter((l) => POSTS.some((p) => p.slug === slug && p.lang === l));
}

/** Все слаги, без повторов. */
export function postSlugs(): string[] {
  return [...new Set(POSTS.map((p) => p.slug))];
}

export const blogPath = (lang: Lang): string => `/${lang}/blog/`;
export const postPath = (slug: string, lang: Lang): string => `/${lang}/blog/${slug}/`;
export const blogUrl = (lang: Lang): string => `${SITE_URL}${blogPath(lang)}`;
export const postUrl = (slug: string, lang: Lang): string => `${SITE_URL}${postPath(slug, lang)}`;

export const BLOG_COPY: Record<
  Lang,
  { nav: string; title: string; description: string; h1: string; lead: string; readMore: string; published: string; coverPhoto: string; dateLocale: string }
> = {
  en: {
    nav: 'Blog',
    title: 'Algarve Travel Blog — Old Town Walks, Guides & Ideas | Algarve Quest',
    description:
      'Self-guided walking routes, maps and practical tips for Faro, Olhão and the Algarve, plus ideas for team building and group days out.',
    h1: 'Algarve travel blog',
    lead: 'Self-guided walks, maps and local stories from Faro, Olhão and Alte.',
    readMore: 'Read the guide',
    published: 'Published',
    coverPhoto: 'Cover photo',
    dateLocale: 'en-GB',
  },
  pt: {
    nav: 'Blog',
    title: 'Blog de viagem no Algarve — passeios, roteiros e ideias | Algarve Quest',
    description:
      'Roteiros autoguiados a pé, mapas e dicas práticas para Faro, Olhão e o Algarve, além de ideias para team building e passeios em grupo.',
    h1: 'Blog de viagem no Algarve',
    lead: 'Passeios autoguiados, mapas e histórias locais de Faro, Olhão e Alte.',
    readMore: 'Ler o roteiro',
    published: 'Publicado',
    coverPhoto: 'Foto de capa',
    dateLocale: 'pt-PT',
  },
  ru: {
    nav: 'Блог',
    title: 'Блог об Алгарве — пешие маршруты, гиды и идеи | Algarve Quest',
    description:
      'Самостоятельные пешие маршруты, карты и практические советы по Фаро, Оляо и Алгарве, а также идеи для тимбилдинга и групповых прогулок.',
    h1: 'Блог об Алгарве',
    lead: 'Самостоятельные прогулки, карты и местные истории из Фаро, Оляо и Алте.',
    readMore: 'Читать',
    published: 'Опубликовано',
    coverPhoto: 'Фото на обложке',
    dateLocale: 'ru-RU',
  },
};

/** Дата для показа: фиксированный формат, одинаковый при пререндере и в браузере. */
export function formatDate(iso: string, lang: Lang): string {
  return new Intl.DateTimeFormat(BLOG_COPY[lang].dateLocale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}
